import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase";
import { Input, MoneyInput, Btn, Select, Checkbox, FileInput, IconBtn, TextLink, Textarea } from "../ui";
import { validateUploadFile, safeNum, parseLocalDate, formatLocalDate, shortId, sanitizeFileName, escapeHtml, escapeFilterValue, ALLOWED_DOC_TYPES, ALLOWED_DOC_EXTENSIONS, statusColors, propertyLabel, fmtDate } from "../utils/helpers";
import { pmError, reportError } from "../utils/errors";
import { readSavedLogin } from "../utils/encryption";
import { printTheme, printFileName } from "../utils/theme";
import { getOrCreateTenantAR, resolveAccountId } from "../utils/accounting";
import { markDirty } from "../utils/unsavedWork";
import { recomputeTenantDocStatus } from "../utils/helpersDb";

// Format all tenants on a property as "John Smith / Jane Doe"
export function formatAllTenants(property) {
  if (!property) return "";
  const names = [property.tenant, property.tenant_2, property.tenant_3, property.tenant_4, property.tenant_5].filter(n => n && n.trim());
  return names.join(" / ") || "";
}

export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, errorCode: "PM-8009" }; }
  static getDerivedStateFromError(error) { return { hasError: true, error, errorCode: "PM-8009" }; }
  componentDidCatch(error, info) {
    // Route through pmError so the event is dedup'd, tagged with company /
    // role, enriched with component stack, and written to error_log.
    pmError("PM-8009", {
      raw: error,
      context: "React ErrorBoundary",
      silent: true,
      meta: { componentStack: (info?.componentStack || "").slice(0, 1500) },
    });
  }
  render() {
  if (this.state.hasError) {
  return (
  <div className="flex items-center justify-center min-h-dvh safe-y bg-subtle-50">
  <div className="text-center p-8 max-w-md">
  <div className="text-5xl mb-4">⚠️</div>
  <h2 className="text-xl font-bold text-subtle-800 mb-2">Something went wrong</h2>
  <p className="text-sm text-subtle-500 mb-2">We've logged this issue automatically.</p>
  <div className="inline-block bg-danger-50 text-danger-700 tnum text-sm px-3 py-1 rounded mb-4">{this.state.errorCode}</div>
  <p className="text-xs text-subtle-400 mb-6">If this keeps happening, share this code with your admin.</p>
  <Btn variant="primary" onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}>Reload App</Btn>
  </div>
  </div>
  );
  }
  return this.props.children;
  }
}

// Lease-lifecycle states that only the tenant Badge renders. They are
// kept out of the shared `statusColors` map (helpers.js) so they cannot
// collide with the work-order / payment / property vocabularies that
// map reuses. `current` and `past` are what PropertyImport writes for
// imported tenants, so without these every imported tenant rendered an
// unstyled grey badge.
const leaseStatusColors = {
  current: "bg-positive-100 text-positive-700",
  past: "bg-neutral-200 text-neutral-700",
  expired: "bg-caution-100 text-caution-700",
  inactive: "bg-neutral-100 text-neutral-500",
};

export function Badge({ status, label }) {
  const color = statusColors[status] || leaseStatusColors[status] || "bg-neutral-100 text-neutral-600";
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${color}`}>{label || status}</span>;
}

export function StatCard({ label, value, sub, color = "text-neutral-800", onClick }) {
  return (
  <div onClick={onClick} className={"bg-white rounded-xl border border-neutral-200 shadow-card p-4" + (onClick ? " cursor-pointer hover:border-brand-200 hover:shadow-pop transition-all" : "")}>
  <div className="text-xs text-neutral-400 font-medium uppercase tracking-widest mb-1">{label}</div>
  <div className={`text-2xl font-display font-bold ${color}`}>{value}</div>
  {sub && <div className="text-xs text-neutral-400 mt-1">{sub}</div>}
  </div>
  );
}

// A saved portal login, shown before anyone can change it: the username, the
// password masked (Show/Hide) and a "Change login" button. `children` are the
// username/password inputs; they appear only when nothing is saved or after
// "Change login", which first hands the saved values to onChange so the
// fields open filled in. The same panel the Utilities page shows
// (Sahil 2026-10-05: "show that and ask the user if they want to edit it").
// `typed` = the form already holds a login (changed earlier, then the step was
// left and reopened): show the fields with it, never the stored one over it.
export function SavedLoginPanel({ row, prefix = "", companyId, onChange, typed = false, className = "", children }) {
  const has = !!(row?.[prefix + "username_encrypted"] && row?.[prefix + "password_encrypted"]);
  const [state, setState] = useState({ status: has ? "loading" : "none" });
  const [showPass, setShowPass] = useState(false);
  const [changing, setChanging] = useState(false);
  useEffect(() => {
    let live = true;
    if (!has) { setState({ status: "none" }); return; }
    setState({ status: "loading" });
    readSavedLogin(row, companyId, prefix)
      .then(r => { if (live) setState(r && r.user != null && r.pass != null ? { status: "shown", user: r.user, pass: r.pass } : { status: "hidden" }); })
      .catch(() => { if (live) setState({ status: "hidden" }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [has, row?.id, prefix, companyId]);
  if (state.status === "none" || changing || typed) return <>{children}</>;
  return (
    <div data-testid="saved-login" className={className + " bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2 space-y-1"}>
      <div className="text-xs font-semibold text-neutral-600">🔐 Saved portal login</div>
      {state.status === "loading" ? <div className="text-sm text-neutral-500">Loading the saved login…</div>
        : state.status === "shown" ? (
          <div className="text-sm space-y-0.5">
            <div><span className="text-neutral-500">Username:</span> <span className="font-medium">{state.user || "—"}</span></div>
            <div><span className="text-neutral-500">Password:</span> <span className="font-medium tnum">{showPass ? (state.pass || "—") : "••••••••"}</span>{" "}
              <TextLink size="xs" onClick={() => setShowPass(v => !v)}>{showPass ? "Hide" : "Show"}</TextLink></div>
          </div>
        ) : <div className="text-sm text-neutral-600">A login is saved, but it can't be shown to you.</div>}
      <div className="flex items-center gap-2 pt-1">
        <span className="text-xs text-neutral-500">Do you want to change it?</span>
        <Btn size="sm" variant="secondary" onClick={() => { onChange(state.status === "shown" ? (state.user || "") : "", state.status === "shown" ? (state.pass || "") : ""); setChanging(true); }}>Change login</Btn>
      </div>
    </div>
  );
}

export function Spinner() {
  return (
  <div className="flex items-center justify-center py-20">
  <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin"></div>
  </div>
  );
}

// Every dialog in the app renders through this, so the dialog contract
// lives here rather than in 23 call sites. Previously it was a bare
// div: no role, no labelling, Escape did nothing, and Tab walked
// straight out of the dialog into the page behind it.
// size: "wide" for a modal that shows pages (the packet as sent).
export function Modal({ title, onClose, children, labelledBy, size = "default" }) {
  const panelRef = React.useRef(null);
  const titleId = React.useRef("modal-title-" + Math.random().toString(36).slice(2, 9)).current;
  // Keep the latest onClose in a ref so the keydown listener never has to
  // re-subscribe (and the focus effect never has to re-run) when a caller
  // passes a new inline onClose each render.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Initial focus + restore-on-close — MOUNT ONLY. This used to depend on
  // [onClose]; callers pass an inline onClose (a fresh function reference every
  // render), so it re-ran on EVERY keystroke and re-focused the first field,
  // yanking the caret out mid-type ("the cursor vanishes while typing" bug).
  // Focus once on open; restore once on close.
  React.useEffect(() => {
    const restoreTo = document.activeElement;
    const panel = panelRef.current;
    if (panel) {
      const first = panel.querySelector(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      (first || panel).focus({ preventScroll: true });
    }
    return () => { if (restoreTo && typeof restoreTo.focus === "function") restoreTo.focus({ preventScroll: true }); };
  }, []);

  // An open dialog is usually a form in progress: hold off the automatic
  // reload into a new deploy while it is up (OPS-9).
  React.useEffect(() => markDirty(), []);

  // Escape-to-close + Tab focus-trap. Stable listener (reads onClose via ref).
  React.useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") { e.stopPropagation(); onCloseRef.current && onCloseRef.current(); return; }
      if (e.key !== "Tab" || !panelRef.current) return;
      // Focus trap. Without it Tab leaves the dialog and lands on the
      // page behind, which is both an accessibility failure and a way
      // to edit the record the dialog is asking about.
      const items = Array.from(panelRef.current.querySelectorAll(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )).filter(el => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const firstEl = items[0], lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  return (
  <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
       onMouseDown={e => { if (e.target === e.currentTarget && onClose) onClose(); }}>
  <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={labelledBy || titleId} tabIndex={-1}
       className={"bg-white rounded-xl border border-neutral-200 shadow-card w-full max-h-[90vh] overflow-y-auto " + (size === "wide" ? "max-w-5xl" : "max-w-lg")}>
  <div className="flex items-center justify-between px-6 py-4 border-b border-brand-50 sticky top-0 bg-white rounded-t-xl">
  <h3 id={titleId} className="font-display font-bold text-neutral-800 text-lg">{title}</h3>
  <IconBtn icon="close" onClick={onClose} aria-label="Close dialog" />
  </div>
  <div className="p-6">{children}</div>
  </div>
  </div>
  );
}

export function ToastContainer({ toasts, removeToast }) {
  return (
  // aria-live, because a toast that is only VISIBLE does not exist for a
  // screen-reader user -- and these carry the confirmation that a posting
  // succeeded or the reason it did not. "polite" on the region so routine
  // successes queue behind whatever is being read; individual error
  // toasts below raise themselves to role="alert".
  <div role="status" aria-live="polite" aria-atomic="false"
       className="fixed bottom-4 right-4 safe-bottom safe-right z-[100] flex flex-col gap-2 max-w-sm">
  {toasts.map(t => (
  <div key={t.id}
       // An error interrupts; a success does not.
       role={t.type === "error" ? "alert" : undefined}
       className={"flex items-start gap-3 px-4 py-3 rounded-xl shadow-pop border backdrop-blur-md animate-slide-up " + (t.type === "error" ? "bg-danger-50 border-danger-200 text-danger-800" : t.type === "warning" ? "bg-warn-50 border-warn-200 text-warn-800" : t.type === "success" ? "bg-success-50 border-success-200 text-success-800" : "bg-white border-brand-100 text-neutral-700")}>
  {t.isError ? (<>
    <span className="material-icons-outlined text-lg mt-0.5">{t.type === "error" ? "error" : "warning"}</span>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-xs tnum px-1.5 py-0.5 rounded font-bold ${t.type === "error" ? "bg-danger-100 text-danger-700" : "bg-warn-100 text-warn-700"}`}>{t.code}</span>
        {t.action === "retry" && <span className="text-xs opacity-70">Try again</span>}
        {t.action === "contact" && <span className="text-xs opacity-70">Contact admin</span>}
      </div>
      <p className="text-sm">{t.message}</p>
    </div>
    <div className="flex items-center gap-1 shrink-0">
      <button onClick={() => reportError(t.code)} aria-label={`Report error ${t.code}`} className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400" title="Report this error">Report</button>
      <button onClick={() => removeToast(t.id)} aria-label="Dismiss" className="text-xs opacity-60 hover:opacity-100 px-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">✕</button>
    </div>
  </>) : (<>
    <span className="material-icons-outlined text-lg mt-0.5">{t.type === "error" ? "error" : t.type === "warning" ? "warning" : t.type === "success" ? "check_circle" : "info"}</span>
    <div className="flex-1 text-sm">{t.message}</div>
    <TextLink tone="neutral" size="xs" underline={false} onClick={() => removeToast(t.id)} className="ml-1"><span className="material-icons-outlined text-sm">close</span></TextLink>
  </>)}
  </div>
  ))}
  </div>
  );
}

// The app's one question dialog. With `fields` it also takes answers
// (an amount, a reason, a choice) and resolves to { key: value }; this
// replaced window.prompt() everywhere on 2026-10-03 -- the browser's own
// box is tiny, unstyled, has no validation, and in the iPhone home-screen
// app it can return nothing, so the button did nothing at all.
//   fields: [{ key, label, type: "text"|"money"|"number"|"textarea"|"select"|"email",
//              placeholder, default, required, options: [{value,label}], help }]
export function ConfirmModal({ config, onConfirm, onCancel }) {
  const fields = Array.isArray(config?.fields) ? config.fields : [];
  const [values, setValues] = useState({});
  useEffect(() => {
    if (!config) return;
    const v = {}; for (const f of fields) v[f.key] = f.default != null ? String(f.default) : "";
    setValues(v);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [config]);
  // Escape answers THIS question "no" and goes no further: the form behind
  // it listens for Escape too and used to close with it (FE-30).
  useEffect(() => {
    if (!config) return undefined;
    const onEsc = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); onCancel(); } };
    window.addEventListener("keydown", onEsc, true);
    return () => window.removeEventListener("keydown", onEsc, true);
  }, [config, onCancel]);
  if (!config) return null;
  const isDanger = config.variant === "danger";
  const missing = fields.some(f => f.required && !String(values[f.key] ?? "").trim());
  const submit = () => { if (missing) return; onConfirm(fields.length ? { ...values } : true); };
  const onKey = (e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") { e.preventDefault(); submit(); } };
  return (
  <div className="fixed inset-0 bg-black/40 z-[90] flex items-center justify-center p-4">
  <div className="bg-white rounded-xl border border-neutral-200 shadow-card w-full max-w-md" role="dialog" aria-modal="true">
  <div className="px-6 py-4 border-b border-brand-50">
  <h3 className="font-display font-bold text-neutral-800 text-lg">{config.title || (isDanger ? "Confirm Action" : fields.length ? "" : "Are you sure?")}</h3>
  </div>
  <div className="px-6 py-5 space-y-3">
  {config.message && <p className="text-sm text-neutral-600 whitespace-pre-line">{config.message}</p>}
  {fields.map((f, i) => {
    const common = { id: "confirm-field-" + f.key, value: values[f.key] ?? "", onKeyDown: onKey, placeholder: f.placeholder || "", autoFocus: i === 0, className: "w-full" };
    const set = (v) => setValues(prev => ({ ...prev, [f.key]: v }));
    return (
      <div key={f.key}>
        {f.label && <label htmlFor={common.id} className="text-xs font-medium text-neutral-500 block mb-1">{f.label}{f.required ? " *" : ""}</label>}
        {f.type === "money" ? <MoneyInput {...common} onChange={v => set(v)} />
          : f.type === "textarea" ? <Textarea {...common} rows={3} onChange={e => set(e.target.value)} />
          : f.type === "select" ? <Select {...common} onChange={e => set(e.target.value)}>{!f.required && <option value="">—</option>}{(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</Select>
          : <Input {...common} type={f.type === "number" ? "number" : f.type === "email" ? "email" : "text"} inputMode={f.type === "number" ? "decimal" : undefined} onChange={e => set(e.target.value)} />}
        {f.help && <div className="text-2xs text-neutral-400 mt-1">{f.help}</div>}
      </div>
    );
  })}
  </div>
  <div className="px-6 py-4 border-t border-brand-50 flex justify-end gap-3">
  <Btn variant="slate" onClick={onCancel}>{config.cancelText || "Cancel"}</Btn>
  <Btn variant={isDanger ? "danger-fill" : "primary"} onClick={submit} disabled={missing}>{config.confirmText || (isDanger ? "Delete" : "Confirm")}</Btn>
  </div>
  </div>
  </div>
  );
}

export function PropertyDropdown({ value, onChange, className = "", required = false, label = "Property", companyId }) {
  const [properties, setProperties] = useState([]);
  useEffect(() => {
  supabase.from("properties").select("id, address, type, status").eq("company_id", companyId).is("archived_at", null).order("address").then(({ data }) => setProperties(data || []));
  }, [companyId]);
  return (
  <div>
  {label && <label className="text-xs font-medium text-neutral-500 uppercase tracking-widest block mb-1">{label} {required && "*"}</label>}
  <Select value={value || ""} onChange={e => { const sel = properties.find(p => p.address === e.target.value); onChange(e.target.value, sel ? sel.id : null); }} className={className} required={required}>
  <option value="">Select property...</option>
  {properties.map(p => <option key={p.id} value={p.address}>{p.address} ({p.type})</option>)}
  </Select>
  </div>
  );
}

export function TenantSelect({ value, onChange, className = "", companyId }) {
  const [tenants, setTenants] = useState([]);
  useEffect(() => {
  supabase.from("tenants").select("id, name, property").eq("company_id", companyId).is("archived_at", null).order("name").then(({ data }) => setTenants(data || []));
  }, [companyId]);
  return (
  <Select value={value || ""} onChange={e => { const sel = tenants.find(t => t.name === e.target.value); onChange(e.target.value, sel); }} className={className}>
  <option value="">Select tenant...</option>
  {tenants.map(t => <option key={t.id} value={t.name}>{t.name}{t.property ? " — " + t.property : ""}</option>)}
  </Select>
  );
}

export function PropertySelect({ value, onChange, className = "", companyId }) {
  const [properties, setProperties] = useState([]);
  useEffect(() => {
  supabase.from("properties").select("id, address, type, tenant, tenant_2, tenant_3, tenant_4, rent, status").eq("company_id", companyId).is("archived_at", null).order("address").then(({ data }) => setProperties(data || []));
  }, [companyId]);
  return (
  <Select value={value || ""} onChange={e => { const sel = properties.find(p => p.address === e.target.value); onChange(e.target.value, sel || null); }} className={className}>
  <option value="">Select property...</option>
  {properties.map(p => <option key={p.id} value={p.address}>{p.address}</option>)}
  </Select>
  );
}

export function DocUploadModal({ onClose, companyId, property, tenant, showToast, onUploaded, isTenantUpload, prospectId = null }) {
  const [form, setForm] = useState({ name: "", type: "Lease", tenant_visible: !!isTenantUpload });
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef();

  async function handleUpload() {
  const file = fileRef.current?.files?.[0];
  if (!file) { showToast("Please select a file", "error"); return; }
  if (!form.name.trim()) { showToast("Document name is required", "error"); return; }
  // Size, type and real bytes: one shared check that refuses when unsure (LIFE-15).
  { const bad = await validateUploadFile(file, { kind: "doc", maxMB: 25 }); if (bad) { showToast(bad, "error"); return; } }
  setUploading(true);
  const fileName = companyId + "/" + shortId() + "/" + sanitizeFileName(file.name);
  const { error: uploadErr } = await supabase.storage.from("documents").upload(fileName, file, { cacheControl: "3600", upsert: false });
  if (uploadErr) { pmError("PM-7002", { raw: uploadErr, context: "document upload" }); setUploading(false); return; }
  const { error: insertErr } = await supabase.from("documents").insert([{
  company_id: companyId, name: form.name.trim(), file_name: fileName, url: fileName,
  property: property || "", tenant: tenant || "", type: form.type,
  tenant_visible: form.tenant_visible, uploaded_at: new Date().toISOString(),
  // A file for someone who is not a tenant yet; it follows them on conversion.
  ...(prospectId ? { prospect_id: prospectId } : {}),
  }]);
  if (insertErr) { pmError("PM-7003", { raw: insertErr, context: "document record insert after upload" }); await supabase.storage.from("documents").remove([fileName]); setUploading(false); return; }
  if (tenant) await recomputeTenantDocStatus(companyId, { tenantName: tenant, property });
  showToast("Document uploaded", "success");
  setUploading(false);
  if (onUploaded) onUploaded();
  onClose();
  }

  return (
  <Modal title="Upload Document" onClose={onClose}>
  <div className="space-y-3">
  {property && <div className="text-xs text-neutral-400">Property: <span className="font-semibold text-neutral-600">{property}</span></div>}
  {tenant && <div className="text-xs text-neutral-400">Tenant: <span className="font-semibold text-neutral-600">{tenant}</span></div>}
  <div>
  <label className="text-xs font-medium text-neutral-400 block mb-1">Document Name *</label>
  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Signed Lease Agreement" />
  </div>
  <div>
  <label className="text-xs font-medium text-neutral-400 block mb-1">Type</label>
  <Select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
  {["Lease","Notice","ID","Insurance","Inspection","Receipt","Other"].map(t => <option key={t} value={t}>{t}</option>)}
  </Select>
  </div>
  <div>
  <label className="text-xs font-medium text-neutral-400 block mb-1">File *</label>
  <div className="flex items-center gap-3 flex-wrap">
  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-50 text-brand-700 border border-brand-200 hover:bg-brand-100 cursor-pointer text-sm font-medium transition-colors">
  <span className="material-icons-outlined text-base">upload_file</span>
  <span>Choose File</span>
  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv" className="hidden" onChange={e => setFileName(e.target.files?.[0]?.name || "")} />
  </label>
  <span className="text-xs text-neutral-500 truncate max-w-[220px]" title={fileName}>{fileName || "No file selected"}</span>
  </div>
  </div>
  {!isTenantUpload && <Checkbox label="Visible to tenant" checked={form.tenant_visible} onChange={e => setForm({ ...form, tenant_visible: e.target.checked })} className="text-xs" />}
  <Btn className="w-full" onClick={handleUpload} disabled={uploading}>{uploading ? "Uploading..." : "Upload"}</Btn>
  </div>
  </Modal>
  );
}

export function generatePaymentReceipt(payment, companyName = "Housify") {
  const receiptDate = fmtDate(payment.date);
  const receiptNum = "REC-" + String(payment.id || shortId()).slice(-8).toUpperCase();

  const html = `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><title>${escapeHtml(printFileName("Payment Receipt", payment.tenant, payment.date, receiptNum))}</title>
<style>
  @media print { @page { margin: 0.5in; } body { -webkit-print-color-adjust: exact; } }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: ${printTheme.inkStrong}; background: ${printTheme.surface}; padding: 40px; }
  .receipt { max-width: 600px; margin: 0 auto; border: 2px solid ${printTheme.borderLight}; border-radius: 12px; overflow: hidden; }
  .header { background: linear-gradient(135deg, ${printTheme.brandDark}, ${printTheme.brandLight}); color: white; padding: 30px; }
  .header h1 { font-size: 24px; margin-bottom: 4px; }
  .header .subtitle { font-size: 13px; opacity: 0.85; }
  .badge { display: inline-block; background: rgba(255,255,255,0.2); border-radius: 20px; padding: 4px 14px; font-size: 12px; font-weight: 600; margin-top: 10px; }
  .body { padding: 30px; }
  .row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid ${printTheme.surfaceMuted}; }
  .row:last-child { border-bottom: none; }
  .label { color: ${printTheme.inkMuted}; font-size: 13px; }
  .value { font-weight: 600; font-size: 14px; text-align: right; }
  .amount-row { background: ${printTheme.successBg}; border-radius: 8px; padding: 16px; margin: 16px 0; display: flex; justify-content: space-between; align-items: center; }
  .amount-row .label { font-size: 15px; font-weight: 600; color: ${printTheme.inkStrong}; }
  .amount-row .value { font-size: 22px; color: ${printTheme.success}; font-weight: 700; }
  .footer { background: ${printTheme.surfaceMuted}; padding: 20px 30px; text-align: center; border-top: 1px solid ${printTheme.borderLight}; }
  .footer p { font-size: 11px; color: ${printTheme.inkSubtle}; }
  .stamp { color: ${printTheme.success}; font-size: 18px; font-weight: 700; border: 3px solid ${printTheme.success}; border-radius: 8px; padding: 6px 20px; display: inline-block; transform: rotate(-3deg); margin-bottom: 10px; }
</style></head>
<body>
<div class="receipt">
  <div class="header">
  <h1>${escapeHtml(companyName)}</h1>
  <div class="subtitle">Payment Receipt</div>
  <div class="badge">Receipt #${receiptNum}</div>
  </div>
  <div class="body">
  <div class="row"><span class="label">Date</span><span class="value">${receiptDate}</span></div>
  <div class="row"><span class="label">Tenant</span><span class="value">${escapeHtml(payment.tenant || "N/A")}</span></div>
  <div class="row"><span class="label">Property</span><span class="value">${escapeHtml(payment.property || "N/A")}</span></div>
  <div class="row"><span class="label">Payment Type</span><span class="value" style="text-transform:capitalize">${escapeHtml(payment.type || "rent")}</span></div>
  <div class="row"><span class="label">Payment Method</span><span class="value" style="text-transform:uppercase">${escapeHtml(payment.method || "N/A")}</span></div>
  <div class="row"><span class="label">Status</span><span class="value" style="text-transform:capitalize">${escapeHtml(payment.status || "paid")}</span></div>
  <div class="amount-row"><span class="label">Amount Paid</span><span class="value">$${safeNum(payment.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span></div>
  </div>
  <div class="footer">
  <div class="stamp">PAID</div>
  <p>This is an electronic receipt generated by ${escapeHtml(companyName)}.</p>
  <p>For questions, contact your property manager.</p>
  </div>
</div>
</body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (win) {
  win.onload = () => { setTimeout(() => win.print(), 500); };
  }
}
